import React, { useState } from 'react';
import { Dropdown, Popover, Whisper } from 'rsuite';

// The "more actions" (⋯) menu at the end of an employee card's action bar. The
// everyday actions sit in the bar itself; the rare and the admin-only ones
// (impersonating, deactivating, deleting) live here.
//
// `items` is a list of { key, label, icon, onSelect, disabled, hint, danger }.
// A danger item is set apart by a divider and only turns red on hover - the
// same rule as .bdc-linkbtn--danger. `hint` explains a disabled item, since a
// greyed-out entry with no reason is a dead end.
//
// The menu portals to the body, but React still bubbles its events through the
// component tree - straight into the card, whose click and Enter both open the
// employee. Both are stopped at the popover.
const stop = (event) => event.stopPropagation();

// `triggerClassName` styles the button that opens it; it carries `is-open`
// while the menu is up, so the trigger can stay shaded like a pressed control.
export default function EmployeeActionsMenu({ items, label, triggerClassName = 'bdc-linkbtn' }) {
  const [open, setOpen] = useState(false);
  const visible = (items || []).filter(Boolean);
  if (visible.length === 0) return null;

  const regular = visible.filter((item) => !item.danger);
  const danger = visible.filter((item) => item.danger);

  const renderItem = (item, onClose) => (
    <Dropdown.Item
      key={item.key}
      eventKey={item.key}
      disabled={item.disabled}
      className={`employee-menu__item${item.danger ? ' employee-menu__item--danger' : ''}`}
      onSelect={() => {
        onClose();
        item.onSelect();
      }}
    >
      <i className={`bi ${item.icon} employee-menu__icon`} aria-hidden="true" />
      <span className="employee-menu__text">
        {item.label}
        {item.hint ? <span className="employee-menu__hint">{item.hint}</span> : null}
      </span>
    </Dropdown.Item>
  );

  return (
    <Whisper
      trigger="click"
      placement="bottomEnd"
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      speaker={({ onClose, left, top, className }, ref) => (
        <Popover
          ref={ref}
          className={`${className} employee-menu`}
          style={{ left, top }}
          full
          onClick={stop}
          onKeyDown={stop}
        >
          <Dropdown.Menu>
            {regular.map((item) => renderItem(item, onClose))}
            {regular.length > 0 && danger.length > 0 ? <Dropdown.Separator /> : null}
            {danger.map((item) => renderItem(item, onClose))}
          </Dropdown.Menu>
        </Popover>
      )}
    >
      <button
        type="button"
        className={`${triggerClassName}${open ? ' is-open' : ''}`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={stop}
        onKeyDown={stop}
      >
        <i className="bi bi-three-dots" aria-hidden="true" />
      </button>
    </Whisper>
  );
}
